import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { T, useGT } from 'gt-react';
import {
  ArrowCounterClockwiseIcon,
  ProhibitIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import type { PricelistCaseFileDTO } from '@erp/rpc';
import { orpc } from '@/api/rpc/client';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { useToast } from 'src/context/ToastContext';
import { Button } from '@/components/ui/button';
import { PricelistCaseFileNote } from './PricelistCaseFileNote';
import { invalidatePricelistCaseFileQueries } from './pricelistCaseFileQueries';
import {
  fileUpdateFailureMessage,
  pricelistCaseFileNameClassName,
} from './pricelistCaseFileRowState';

function unexpectedFailureMessage(gt: ReturnType<typeof useGT>): string {
  return gt('Something went wrong.', {
    context: 'generic toast message for an unexpected failure',
  });
}

function FileStatusIcon({ deprecated }: { deprecated: boolean }) {
  if (deprecated) {
    return <ArrowCounterClockwiseIcon className="h-3.5 w-3.5" />;
  }
  return <ProhibitIcon className="h-3.5 w-3.5" />;
}

function FileStatusLabel({ deprecated }: { deprecated: boolean }) {
  if (deprecated) {
    return (
      <T context="button that reinstates a deprecated pricelist case file">
        Reinstate
      </T>
    );
  }
  return (
    <T context="button that marks a pricelist case file as not to be used">
      Deprecate
    </T>
  );
}

function showFileStatusToast(
  showToast: ReturnType<typeof useToast>['showToast'],
  gt: ReturnType<typeof useGT>,
  deprecated: boolean,
) {
  if (deprecated) {
    showToast({
      title: gt('File deprecated', {
        context: 'toast title when a pricelist case file is marked deprecated',
      }),
      message: gt('It stays attached and in history, but should not be used.', {
        context:
          'toast message when a pricelist case file is marked deprecated',
      }),
      color: 'success',
    });
    return;
  }
  showToast({
    title: gt('File reinstated', {
      context:
        'toast title when a deprecated pricelist case file is reinstated',
    }),
    message: gt('It is active on this price change again.', {
      context:
        'toast message when a deprecated pricelist case file is reinstated',
    }),
    color: 'success',
  });
}

export function PricelistCaseFileStatusButton({
  caseId,
  file,
}: {
  caseId: string;
  file: PricelistCaseFileDTO;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const updateMutation = useMutation(
    orpc.inventoryManagement.pricelistCases.updateFile.mutationOptions(),
  );

  async function persistDeprecated() {
    const deprecated = !file.deprecated;
    try {
      await updateMutation.mutateAsync({
        caseId,
        documentId: file.documentId,
        deprecated,
      });
      invalidatePricelistCaseFileQueries(queryClient);
      showFileStatusToast(showToast, gt, deprecated);
    } catch (error) {
      showToast({
        title: gt('Could not update the file', {
          context:
            'toast title when updating a pricelist case file status fails',
        }),
        message: fileUpdateFailureMessage(error, unexpectedFailureMessage(gt)),
        color: 'error',
      });
    }
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 shrink-0 px-2 text-xs"
      type="button"
      disabled={updateMutation.isPending}
      onClick={() => void persistDeprecated()}
    >
      <FileStatusIcon deprecated={file.deprecated} />
      <FileStatusLabel deprecated={file.deprecated} />
    </Button>
  );
}

export function PricelistCaseFileDetachButton({
  caseId,
  file,
}: {
  caseId: string;
  file: PricelistCaseFileDTO;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const detachMutation = useMutation(
    orpc.inventoryManagement.pricelistCases.detachFile.mutationOptions(),
  );

  async function handleDetach() {
    try {
      await detachMutation.mutateAsync({ caseId, documentId: file.documentId });
      setConfirmOpen(false);
      invalidatePricelistCaseFileQueries(queryClient);
      showToast({
        title: gt('File detached', {
          context: 'toast title when a file is removed from a pricelist case',
        }),
        message: gt('It is back in the upload list, unchanged.', {
          context: 'toast message when a file is removed from a pricelist case',
        }),
        color: 'success',
      });
    } catch (error) {
      showToast({
        title: gt('Could not detach the file', {
          context: 'toast title when detaching a pricelist case file fails',
        }),
        message: fileUpdateFailureMessage(error, unexpectedFailureMessage(gt)),
        color: 'error',
      });
    }
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-7 shrink-0 whitespace-nowrap px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
        type="button"
        disabled={detachMutation.isPending}
        onClick={() => setConfirmOpen(true)}
      >
        <TrashIcon className="h-4 w-4" />
        <T context="button that detaches a file from a pricelist case">
          Detach
        </T>
      </Button>
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void handleDetach()}
        title={
          <T context="confirm dialog title when detaching a pricelist case file">
            Detach this file?
          </T>
        }
        message={gt('It will go back to the upload list, unchanged.', {
          context: 'confirm dialog body when detaching a pricelist case file',
        })}
        confirmText={gt('Detach', {
          context: 'confirm button that detaches a pricelist case file',
        })}
        cancelText={gt('Cancel', {
          context: 'cancel button that keeps a pricelist case file',
        })}
        loading={detachMutation.isPending}
        dangerous
        closeOnConfirm={false}
      />
    </>
  );
}

export function PricelistCaseFileToolbar({
  caseId,
  file,
  canEdit,
}: {
  caseId: string;
  file: PricelistCaseFileDTO;
  canEdit: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`min-w-0 text-base font-medium ${pricelistCaseFileNameClassName(file.deprecated)}`}
          title={file.name}
        >
          {file.name}
        </span>
        {file.deprecated ? (
          <span className="w-fit shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            <T context="badge on a pricelist case file that should not be used">
              Deprecated
            </T>
          </span>
        ) : null}
      </div>
      <PricelistCaseFileNote
        key={file.documentId}
        caseId={caseId}
        file={file}
        canEdit={canEdit}
      />
    </div>
  );
}
